import React from 'react';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';

import formatPrice from 'services/helpers/formatPrice';

import DetailsSectionCard, { SECTION_BORDER } from './DetailsSectionCard';
import Pill, { PillVariant } from './Pill';

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr) 64px 88px',
    gap: '12px',
    padding: theme.spacing(0, 3),
    minHeight: 48,
    alignItems: 'center',
    fontSize: 14,
    borderBottom: SECTION_BORDER,
  },

  head: {
    minHeight: 36,
    backgroundColor: theme.palette.background.default,
    fontSize: 12,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: theme.palette.secondary.contrastText,
    borderBottom: 'none',
  },

  total: {
    borderBottom: 'none',
    color: theme.palette.text.secondary,
  },

  bold: {
    fontWeight: 'bold',
  },

  right: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },

  status: {
    textTransform: 'capitalize',
  },

  totalLabels: {
    color: theme.palette.text.primary,
  },

  totalAmount: {
    color: theme.palette.primary.main,
  },

  empty: {
    padding: theme.spacing(2, 3),
  },
}));

const getStatusVariant = (status: string): PillVariant => {
  switch (status?.toUpperCase()) {
    case 'SHIPPED':
      return 'success';
    case 'REFUNDED':
      return 'danger';
    default:
      return 'neutral';
  }
};

interface Props {
  summaries: any[];
  currency: string;
}

const ShipmentSummariesSection = ({ summaries, currency }: Props) => {
  const classes = useStyles();

  const totals = (summaries ?? []).reduce(
    (acc, summary) => ({
      labels: acc.labels + (summary.labels ?? 0),
      amount: acc.amount + (summary.amount ?? 0),
    }),
    { labels: 0, amount: 0 }
  );

  return (
    <DetailsSectionCard
      id="user-details-shipments"
      title="Shipment Summaries"
      count={summaries?.length ?? 0}>
      {!summaries?.length ? (
        <Typography className={classes.empty} color="textSecondary">
          No shipment summaries found
        </Typography>
      ) : (
        <div role="table" aria-label="Shipment Summaries">
          <div role="row" className={classNames(classes.row, classes.head)}>
            <span role="columnheader">Carrier</span>
            <span role="columnheader">Status</span>
            <span role="columnheader" className={classes.right}>
              Labels
            </span>
            <span role="columnheader" className={classes.right}>
              Amount
            </span>
          </div>
          {summaries.map((summary: any, index: number) => (
            <div key={index} role="row" className={classes.row}>
              <span role="cell" className={classes.bold}>
                {summary.provider}
              </span>
              <span role="cell">
                <Pill variant={getStatusVariant(summary.status)} className={classes.status}>
                  {String(summary.status ?? '')
                    .replace(/_/g, ' ')
                    .toLowerCase()}
                </Pill>
              </span>
              <span role="cell" className={classes.right}>
                {summary.labels}
              </span>
              <span role="cell" className={classNames(classes.right, classes.bold)}>
                {formatPrice(Number(summary.amount ?? 0).toFixed(2), { currency })}
              </span>
            </div>
          ))}
          <div role="row" className={classNames(classes.row, classes.total)}>
            <span role="rowheader" className={classes.bold}>
              Total
            </span>
            <span role="cell" />
            <span
              role="cell"
              className={classNames(classes.right, classes.bold, classes.totalLabels)}>
              {totals.labels}
            </span>
            <span
              role="cell"
              className={classNames(classes.right, classes.bold, classes.totalAmount)}>
              {formatPrice(totals.amount.toFixed(2), { currency })}
            </span>
          </div>
        </div>
      )}
    </DetailsSectionCard>
  );
};

export default ShipmentSummariesSection;
